import React, { useEffect, useState } from 'react';
import { ScrollText, Filter } from 'lucide-react';
import { getAuditLogs, formatAuditAction, formatTimestamp } from '../../staffPlatformService';
import { AuditAction, AuditLogEntry } from '../../types/staffPlatform';

const AUDIT_ACTIONS: AuditAction[] = [
  'staff.login',
  'staff.logout',
  'staff.role_updated',
  'vendor.submitted',
  'vendor.updated',
  'vendor.approved',
  'vendor.rejected',
  'vendor.deleted',
  'vendor.sharepoint_sync',
  'document.uploaded',
  'document.deleted',
  'settings.email_updated',
];

const AuditLogPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);

  const loadLogs = async (filter?: AuditAction) => {
    setLoading(true);
    setError(null);
    const result = await getAuditLogs(200, filter);
    if (result.success && result.logs) {
      setLogs(result.logs);
    } else {
      setError(result.error || 'Failed to load audit logs');
    }
    setLoading(false);
  };

  useEffect(() => {
    const filter = actionFilter === 'all' ? undefined : (actionFilter as AuditAction);
    loadLogs(filter);
  }, [actionFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sunbeth-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
          Track staff and vendor activity across the platform.
        </p>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex items-center gap-3">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sunbeth-orange-500 focus:border-transparent text-sm"
          >
            <option value="all">All Actions</option>
            {AUDIT_ACTIONS.map((action) => (
              <option key={action} value={action}>
                {formatAuditAction(action)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatTimestamp(log.timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sunbeth-orange-100 text-sunbeth-orange-800">
                      {formatAuditAction(log.action)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>{log.actorName}</div>
                    <div className="text-xs text-gray-500">{log.actorEmail}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {log.targetLabel || log.targetId || '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                    {log.details || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {logs.length === 0 && !error && (
          <div className="text-center py-12">
            <ScrollText className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">No audit log entries found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogPage;
